import type { FC, ReactNode } from 'react'
import type { Testimonial } from '@/types'

import { useCallback, useEffect, useState } from 'react'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useInView } from '@/hooks/use-in-view'
import { useReducedMotion } from '@/hooks/use-reduced-motion'
import { Eyebrow } from '@/components/generals/eyebrow'
import { Container } from '@/components/generals/container'

const INTERVAL = 8000

interface TestimonialsProps {
    testimonials: Array<Testimonial>
}

const initials = (name: string): string => {
    return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part[0]?.toUpperCase() ?? '')
        .join('')
}

export const Testimonials: FC<TestimonialsProps> = ({ testimonials }): ReactNode => {
    const [active, setActive] = useState(0)
    const [paused, setPaused] = useState(false)
    const [interacted, setInteracted] = useState(false)
    const [sectionRef, inView] = useInView<HTMLElement>()
    const reduced = useReducedMotion()
    const count = testimonials.length

    const go = useCallback((direction: number) => {
        setInteracted(true)
        setActive(current => (current + direction + count) % count)
    }, [count])

    useEffect(() => {
        if (count < 2 || paused || reduced || !inView || interacted) return

        const timer = window.setTimeout(() => setActive(current => (current + 1) % count), INTERVAL)

        return () => window.clearTimeout(timer)
    }, [active, count, paused, reduced, inView, interacted])

    if (!count) return null

    const current = testimonials[Math.min(active, count - 1)]
    const autoplaying = count > 1 && !paused && !reduced && inView && !interacted

    return (
        <section
            ref={sectionRef}
            id='testimonials'
            aria-labelledby='testimonials-title'
            aria-roledescription='carousel'
            className='relative overflow-hidden bg-burgundy text-cream'
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onFocus={() => setPaused(true)}
            onBlur={() => setPaused(false)}
        >
            <Container className='py-28 md:py-40'>
                <div className='grid gap-14 lg:grid-cols-12 lg:gap-10'>
                    <div className='flex flex-col lg:col-span-4'>
                        <Eyebrow tone='light'>Customer stories</Eyebrow>

                        <h2 id='testimonials-title' className='mt-6 max-w-xs font-heading text-display-sm font-medium text-cream'>
                            Finance teams, in their own words.
                        </h2>

                        {count > 1 && (
                            <div className='mt-10 flex items-center gap-4 lg:mt-auto'>
                                <button
                                    type='button'
                                    onClick={() => go(-1)}
                                    aria-label='Previous story'
                                    className='flex size-11 items-center justify-center rounded-full border border-cream/20 text-cream transition-colors duration-300 hover:border-cream/50 hover:bg-cream/5'
                                >
                                    <ArrowLeft aria-hidden='true' className='size-4' />
                                </button>

                                <button
                                    type='button'
                                    onClick={() => go(1)}
                                    aria-label='Next story'
                                    className='flex size-11 items-center justify-center rounded-full border border-cream/20 text-cream transition-colors duration-300 hover:border-cream/50 hover:bg-cream/5'
                                >
                                    <ArrowRight aria-hidden='true' className='size-4' />
                                </button>

                                <span className='font-mono text-xs tabular-nums text-cream/50'>
                                    {String(active + 1).padStart(2, '0')} / {String(count).padStart(2, '0')}
                                </span>
                            </div>
                        )}
                    </div>

                    <div className='lg:col-span-8'>
                        <figure
                            key={current.id}
                            aria-live={interacted ? 'polite' : 'off'}
                            aria-label={`Story ${active + 1} of ${count}`}
                            className='animate-in fade-in slide-in-from-bottom-3 duration-700'
                        >
                            <blockquote className='font-heading text-[clamp(1.6rem,3vw,2.9rem)] font-medium leading-[1.15] tracking-[-0.025em] text-cream'>
                                <span aria-hidden='true' className='text-beige'>&ldquo;</span>
                                {current.quote}
                                <span aria-hidden='true' className='text-beige'>&rdquo;</span>
                            </blockquote>

                            <figcaption className='mt-10 flex items-center gap-4'>
                                {current.avatar ? (
                                    // eslint-disable-next-line @next/next/no-img-element
                                    <img
                                        src={current.avatar}
                                        alt={current.avatarAlt ?? current.name}
                                        width={48}
                                        height={48}
                                        loading='lazy'
                                        className='size-12 rounded-full object-cover'
                                    />
                                ) : (
                                    <span aria-hidden='true' className='flex size-12 items-center justify-center rounded-full bg-cream/10 font-medium text-beige'>
                                        {initials(current.name)}
                                    </span>
                                )}

                                <span>
                                    <span className='block font-medium text-cream'>{current.name}</span>
                                    <span className='block text-sm text-cream/60'>
                                        {[current.role, current.company].filter(Boolean).join(', ')}
                                    </span>
                                </span>
                            </figcaption>
                        </figure>

                        {count > 1 && (
                            <div className='mt-14 flex gap-2' aria-hidden='true'>
                                {testimonials.map((testimonial, index) => (
                                    <span key={testimonial.id} className='relative h-px flex-1 overflow-hidden bg-cream/15'>
                                        <span
                                            key={`${testimonial.id}-${active}`}
                                            className={cn(
                                                'absolute inset-y-0 left-0 bg-cream',
                                                index < active && 'w-full',
                                                index > active && 'w-0',
                                                index === active && (autoplaying ? 'animate-[testimonial-progress_8s_linear_forwards]' : 'w-full')
                                            )}
                                        />
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </Container>
        </section>
    )
}
